// My customers beyond the Worklist and History (Shipping, Pre-route, the System log): the person's
// assignments as a filter, on or off per page — `v.my` in the address, kept for this browser tab, opened
// as `openingMine` says (the System log off by default). The chip is the Worklist's: "My customers (N)"
// with All customers and ×, or "Show my customers (N)" to put it back. The rules are `assignments.ts`'s.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { myCustomersChip, openingMine } from "@app/shared";
import type { MyCustomerFilter, MyCustomersPage } from "@app/shared";
import { useMyView } from "./data";

const TAB_MINE = (page: MyCustomersPage) => `routing.mine.${page}`;
function rememberedMine(page: MyCustomersPage): unknown {
  try {
    return sessionStorage.getItem(TAB_MINE(page)) ?? undefined;
  } catch {
    return undefined;
  }
}
function keepMine(page: MyCustomersPage, mine: boolean) {
  try {
    sessionStorage.setItem(TAB_MINE(page), mine ? "1" : "0");
  } catch {
    // Not kept: the page's tab opens it as its default.
  }
}

export interface MyCustomers {
  /** The person has customers: only then is there a chip, and `v.my`. */
  hasMine: boolean;
  /** On: the page shows their customers' rows only. */
  mine: boolean;
  setMine(on: boolean): void;
  filter: MyCustomerFilter;
  /** Their customer codes: the chip's N. */
  count: number;
  labels: readonly string[];
}

/** A page's My customers: decided once the person's view is read, then in the address and this tab. */
export function useMyCustomers(page: MyCustomersPage): MyCustomers {
  const my = useMyView();
  const search: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();
  const keys = my.data?.customers.keys ?? [];
  const hasMine = keys.length > 0;
  // Null until the person's view is read; unreadable: off.
  const [mine, setMine] = useState<boolean | null>(null);
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || my.isPending) return;
    opened.current = true;
    setMine(my.data ? openingMine(page, hasMine, search["v.my"], rememberedMine(page)) : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the person's view is read
  }, [my.isPending]);
  useEffect(() => {
    if (mine === null || !hasMine) return;
    keepMine(page, mine);
    void navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => ({ ...prev, "v.my": mine ? "1" : "0" })) as never,
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the choice only
  }, [mine, hasMine]);
  return {
    hasMine,
    mine: !!mine && hasMine,
    setMine,
    filter: { keys, spellings: my.data?.customers.spellings ?? [] },
    count: keys.length,
    labels: my.data?.customers.labels ?? [],
  };
}

/**
 * The My customers chip: "My customers (N)" with All customers and × while on, "Show my customers (N)"
 * while off, nothing for someone with no customers (`myCustomersChip`).
 */
export function MyCustomersChip({
  hasMine,
  mine,
  count,
  labels,
  onChange,
  note = "",
}: {
  hasMine: boolean;
  mine: boolean;
  count: number;
  labels: readonly string[];
  onChange: (on: boolean) => void;
  /** Said after the labels on hover (History: how the workbook's rows count). */
  note?: string;
}) {
  const chip = myCustomersChip(hasMine, mine);
  const title = `Your customers (Admin → Customer assignments): ${labels.join(", ")}.${note ? ` ${note}` : ""}`;
  if (chip === "show")
    return (
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground hover:bg-muted inline-flex items-center rounded-full border border-dashed border-sky-300 px-2.5 py-0.5 text-xs"
        title={title}
        onClick={() => onChange(true)}
        data-testid="show-my-customers"
      >
        Show my customers ({count})
      </button>
    );
  if (chip === "on")
    return (
      <span
        className="inline-flex max-w-full items-center gap-1 rounded-full border border-sky-300 bg-sky-50 py-0.5 pr-1 pl-2.5 text-xs"
        title={title}
        data-testid="my-customers-chip"
      >
        <span className="truncate">My customers ({count})</span>
        <button
          type="button"
          className="text-primary rounded-full px-1 hover:underline"
          onClick={() => onChange(false)}
        >
          All customers
        </button>
        <button
          type="button"
          aria-label="Remove My customers"
          className="hover:bg-background rounded-full p-0.5"
          onClick={() => onChange(false)}
        >
          <XIcon className="size-3" aria-hidden />
        </button>
      </span>
    );
  return null;
}
